<template>
  <nav class="sidebar-nav">
    <div class="list-group list-group-flush">
      <template v-for="node in filteredTree" :key="node.Id">
        <RouterLink
          v-if="!hasChildren(node)"
          :to="mapPath(node.Path)"
          class="list-group-item list-group-item-action bg-transparent text-light border-0 py-2 px-3 d-flex align-items-center"
        >
          <span class="text-truncate">{{ getMenuTitle(node) }}</span>
        </RouterLink>

        <div v-else class="list-group-item bg-transparent text-light border-0 px-0 py-0">
          <button class="btn-toggle w-100 d-flex align-items-center gap-2 py-2 px-3" :class="{ 'parent-active': hasActiveChild(node) }" @click="toggle(node)">
            <span class="text-truncate">{{ getMenuTitle(node) }}</span>
            <i class="bi ms-auto" :class="isOpen(node) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
          </button>
          <div v-show="isOpen(node)" class="list-group list-group-flush ms-2">
            <template v-for="c in node.children" :key="c.Id">
              <RouterLink
                v-if="!hasChildren(c)"
                :to="mapPath(c.Path)"
                class="list-group-item list-group-item-action bg-transparent text-light border-0 py-2 px-3 d-flex align-items-center"
              >
                <span class="text-truncate">{{ getMenuTitle(c) }}</span>
              </RouterLink>

              <div v-else class="list-group-item bg-transparent text-light border-0 px-0 py-0">
                <button class="btn-toggle w-100 d-flex align-items-center gap-2 py-2 px-3" :class="{ 'parent-active-child': hasActiveChild(c) }" @click="toggle(c)">
                  <span class="text-truncate">{{ getMenuTitle(c) }}</span>
                  <i class="bi ms-auto" :class="isOpen(c) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                </button>
                <div v-show="isOpen(c)" class="list-group list-group-flush ms-3">
                  <template v-for="g in c.children" :key="g.Id">
                    <RouterLink
                      v-if="!hasChildren(g)"
                      :to="mapPath(g.Path)"
                      class="list-group-item list-group-item-action bg-transparent text-light border-0 py-2 px-3 d-flex align-items-center"
                    >
                      <span class="text-truncate">{{ getMenuTitle(g) }}</span>
                    </RouterLink>

                    <div v-else class="list-group-item bg-transparent text-light border-0 px-0 py-0">
                      <button class="btn-toggle w-100 d-flex align-items-center gap-2 py-2 px-3" :class="{ 'parent-active-child': hasActiveChild(g) }" @click="toggle(g)">
                        <span class="text-truncate">{{ getMenuTitle(g) }}</span>
                        <i class="bi ms-auto" :class="isOpen(g) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                      </button>
                      <div v-show="isOpen(g)" class="list-group list-group-flush ms-4">
                        <RouterLink
                          v-for="gg in g.children"
                          :key="gg.Id"
                          :to="mapPath(gg.Path)"
                          class="list-group-item list-group-item-action bg-transparent text-light border-0 py-2 px-3 d-flex align-items-center"
                        >
                          <span class="text-truncate">{{ getMenuTitle(gg) }}</span>
                        </RouterLink>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>
  </nav>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, watch, computed } from 'vue';
import { useRoute } from 'vue-router';

const tree = ref([]);
const openSet = ref(new Set());
const route = useRoute();
let currentUid = null;
let cacheKey = null;

// 씨앤텍차이나 메뉴명 고정 매핑 (언어 설정과 관계없이 항상 고정)
const cntechChinaFixedTitles = {
  'cntechchina': 'C&Tech China',
  '씨앤텍차이나': 'C&Tech China',
  'codemanage': 'Code DB',
  '자재코드관리': 'Code DB',
  'cntechchina-purchase-orders': 'Purchase Order',
  '구매오더관리': 'Purchase Order',
  'cntechchina-suppliers': 'Supplier DB',
  '공급처관리': 'Supplier DB',
  'cntechchina-prices': 'Price DB',
  '단가관리': 'Price DB',
  'cntechchina-po-management': 'PO Management',
  'PO관리': 'PO Management'
};

// 메뉴 타이틀 변환 함수
function getMenuTitle(node) {
  const key = node.Key?.toLowerCase();
  const title = node.Title?.toLowerCase();

  // 씨앤텍차이나 관련 메뉴는 고정 타이틀 사용
  const fixedTitle = cntechChinaFixedTitles[key] || cntechChinaFixedTitles[title];
  if (fixedTitle) {
    return fixedTitle;
  }

  // 일반 메뉴는 기존 로직 사용
  return node.Title || node.Key;
}

// 성능 최적화: 경로 사전 계산 (Pre-calculation)
const pathToNode = new Map();      // '/attendance/annual/adwin' -> node
const nodeToParents = new Map();   // node.Id -> [parent1, parent2, ...]
const nodeToPath = new Map();      // node.Id -> '/attendance/annual/adwin'

// 필터링된 트리 (현재는 모든 항목 표시)
const filteredTree = computed(() => {
  return tree.value;
});

function hasChildren(n){ return Array.isArray(n.children) && n.children.length > 0; }
// key 는 렌더마다 같은 값이어야 한다. 랜덤을 쓰면 다시 그릴 때 DOM이 통째로 새로 만들어져
//  클릭하는 순간 항목 위치가 바뀔 수 있다(= 엉뚱한 메뉴로 이동).
function keyOf(n){ return n?.Id ?? n?.Key ?? n?.Path ?? n?.Title ?? ''; }
function isOpen(n){ return openSet.value.has(keyOf(n)); }
function toggle(n){ const k=keyOf(n); if(openSet.value.has(k)) openSet.value.delete(k); else openSet.value.add(k); }

function mapPath(p){
  if (!p) return '/';
  // 구체적인 경로를 먼저 체크 (근태 관리 등)
  // 애드윈 근태 관리
  if (p.endsWith('/adwin/attendance/index.html')) return '/attendance/annual/adwin';
  if (p.endsWith('/adwin/attendance-annual.html')) return '/attendance/annual/adwin';
  if (p.endsWith('/adwin/attendance-annual2.html')) return '/attendance/annual2/adwin';
  if (p.endsWith('/adwin/attendance-history.html')) return '/attendance/history/adwin';
  if (p.endsWith('/adwin/attendance-approval.html')) return '/attendance/approval/adwin';
  // 씨엔텍 근태 관리
  if (p.endsWith('/cntech/attendance/index.html')) return '/attendance/annual/cntech';
  if (p.endsWith('/cntech/attendance-annual.html')) return '/attendance/annual/cntech';
  if (p.endsWith('/cntech/attendance-annual2.html')) return '/attendance/annual2/cntech';
  if (p.endsWith('/cntech/attendance-history.html')) return '/attendance/history/cntech';
  if (p.endsWith('/cntech/attendance-approval.html')) return '/attendance/approval/cntech';
  // 일반 경로 매핑
  if (p.endsWith('/index.html')) return '/';
  if (p.endsWith('/login.html')) return '/login';
  if (p.endsWith('/admin.html')) return '/admin';
  if (p.endsWith('/admin-logs.html')) return '/admin/logs';
  if (p.endsWith('/admin-announce.html')) return '/admin/announce';
  if (p.endsWith('/admin/annual-management.html')) return '/admin/annual-management';
  if (p === '/admin/opengraph' || p.endsWith('/admin/opengraph')) return '/admin/opengraph';
  if (p.endsWith('/user-approval.html')) return '/user-approval';
  if (p.endsWith('/user-create.html')) return '/user-create';
  if (p.endsWith('/user-list.html')) return '/user-list';
  if (p.endsWith('/reports.html')) return '/reports';
  if (p.endsWith('/board.html')) return '/board';
  // 기존 경로 (하위 호환성)
  if (p.endsWith('/attendance-annual.html')) return '/attendance/annual';
  if (p.endsWith('/attendance-history.html')) return '/attendance/history';
  if (p.endsWith('/attendance-approval.html')) return '/attendance/approval';
  // 직원 관리
  if (p.endsWith('/adwin/employee-management.html')) return '/personnel/employees/adwin';
  if (p.endsWith('/cntech/employee-management.html')) return '/personnel/employees/cntech';
  if (p.endsWith('/employee-management.html')) return '/personnel/employees';
  if (p.endsWith('/adwin/certificate.html')) return '/personnel/certificates/adwin';
  // 씨앤텍차이나
  if (p.endsWith('/cntechchina/index.html')) return '/cntechchina';
  if (p === '/cntechchina/codemanage' || p.endsWith('/cntechchina/codemanage')) return '/cntechchina/codemanage';
  if (p === '/cntechchina/purchase-orders' || p.endsWith('/cntechchina/purchase-orders')) return '/cntechchina/purchase-orders';
  if (p === '/cntechchina/suppliers' || p.endsWith('/cntechchina/suppliers')) return '/cntechchina/suppliers';
  if (p === '/cntechchina/prices' || p.endsWith('/cntechchina/prices')) return '/cntechchina/prices';
  if (p === '/cntechchina/po-management' || p.endsWith('/cntechchina/po-management')) return '/cntechchina/po-management';
  // 구매오더 관리 (영업지원본부)
  if (p.endsWith('/sales-support/purchase-orders.html')) return '/sales-support/purchase-orders';
  if (p === '/sales-support/purchase-orders' || p.endsWith('/sales-support/purchase-orders')) return '/sales-support/purchase-orders';
  return p;
}

function normalizePath(p){
  const m = mapPath(p);
  if (m === '/user-create') return 'user-create';
  if (m === '/user-list') return 'user-list';
  if (m === '/user-approval') return 'user-approval';
  if (m === '/admin') return 'menu-admin';
  if (m === '/admin/logs') return 'admin-logs';
  if (m === '/admin/announce') return 'admin-announce';
  if (m === '/board') return 'board';
  if (m === '/attendance/annual') return 'attendance-annual';
  if (m === '/attendance/history') return 'attendance-history';
  if (m === '/attendance/approval') return 'attendance-approval';
  return '';
}

function reorderAdminChildren(rootNodes){
  const desired = ['user-create', 'user-approval', 'user-list', 'admin-announce', 'admin-logs', 'menu-admin'];
  const orderIndex = Object.fromEntries(desired.map((k,i)=>[k,i]));
  const walk = (nodes) => {
    nodes.forEach(n => {
      if (Array.isArray(n.children) && n.children.length){
        const flagged = n.children.some(c => orderIndex.hasOwnProperty(normalizePath(c.Path)));
        if (flagged){
          n.children.sort((a,b) => {
            const ka = normalizePath(a.Path);
            const kb = normalizePath(b.Path);
            const ia = (ka in orderIndex) ? orderIndex[ka] : Number.MAX_SAFE_INTEGER;
            const ib = (kb in orderIndex) ? orderIndex[kb] : Number.MAX_SAFE_INTEGER;
            if (ia !== ib) return ia - ib;
            return (a.DisplayOrder - b.DisplayOrder) || (a.Id - b.Id);
          });
        }
        walk(n.children);
      }
    });
  };
  walk(rootNodes);
  return rootNodes;
}

// 경로 매핑 빌드 함수 (메뉴 로드 시 1회만 실행)
function buildPathMappings(nodes, parents = []) {
  for (const node of nodes) {
    const mappedPath = mapPath(node.Path);

    // path -> node 매핑
    pathToNode.set(mappedPath, node);

    // node -> parents 매핑
    nodeToParents.set(node.Id, [...parents]);

    // node -> path 매핑
    nodeToPath.set(node.Id, mappedPath);

    // 재귀: 자식 노드 처리
    if (Array.isArray(node.children) && node.children.length > 0) {
      buildPathMappings(node.children, [...parents, node]);
    }
  }
}

function injectAdminLogsNode(rootNodes){
  const makeNode = (parent) => ({ Id: 900000 + Math.floor(Math.random()*1000), Key: 'admin-logs', Title: '로그 파일 보기', Path: '/admin-logs.html', ParentId: parent?.Id || null, DisplayOrder: 9999, children: [] });
  const walk = (nodes) => {
    for (const n of nodes){
      if (Array.isArray(n.children) && n.children.length){
        const hasAdmin = n.children.some(c => mapPath(c.Path) === '/admin');
        if (hasAdmin){ n.children.push(makeNode(n)); return true; }
        if (walk(n.children)) return true;
      }
    }
    return false;
  };
  const injected = walk(rootNodes);
  if (!injected) rootNodes.push(makeNode(null));
  return rootNodes;
}
function buildTree(items){
  const byId = new Map();
  items.forEach(m => byId.set(m.Id, { ...m, children: [] }));
  const roots = [];
  items.forEach(m => {
    const n = byId.get(m.Id);
    if (m.ParentId && byId.has(m.ParentId)) byId.get(m.ParentId).children.push(n);
    else roots.push(n);
  });
  const sort = arr => { arr.sort((a,b)=> (a.DisplayOrder-b.DisplayOrder)||(a.Id-b.Id)); arr.forEach(n=>sort(n.children)); };
  sort(roots);
  return roots;
}

function filterMenuItems(nodes){
  // 모든 메뉴 항목 표시 (필터링 없음)
  return nodes;
}

async function currentUserId(){
  const token = localStorage.getItem('token');
  if (token){
    try{ const payload = JSON.parse(atob(token.split('.')[1])); if (payload?.sub) return payload.sub; }catch(_e){}
    try{
      const r = await fetch('/api/auth/me', { credentials:'include', cache:'no-store', headers: { Authorization: 'Bearer ' + token } });
      const j = await r.json().catch(()=>null);
      if (j?.user?.id != null) return j.user.id;
      if (j?.user?.Id != null) return j.user.Id;
    }catch(_e){}
  }
  return 'me';
}

// ── 디버그: 메뉴가 안 보일 때 원인 추적용 (localStorage.sidebarDebug='1' 이면 트리 전체 출력) ──
const SB_DEBUG = () => { try { return localStorage.getItem('sidebarDebug') === '1'; } catch { return false; } };
function sbLog(...a){ console.log('[Sidebar]', ...a); }
function findByKey(nodes, key){
  for (const n of (nodes || [])) {
    if (n.Key === key) return n;
    const hit = findByKey(n.children, key);
    if (hit) return hit;
  }
  return null;
}
function sbReport(label, menus, treeNodes){
  const inMenus = (menus || []).some((m) => m.Key === 'admin-api-keys');
  const inTree = !!findByKey(treeNodes, 'admin-api-keys');
  sbLog(`${label}: 메뉴 ${menus ? menus.length : '-'}개 · 트리 루트 ${(treeNodes||[]).length}개 · 인증키관리(menus=${inMenus}, tree=${inTree})`);
  if (SB_DEBUG()) sbLog('  트리 전체:', JSON.parse(JSON.stringify(treeNodes || [])));
}

/**
 * 라우터 가드(main.js 의 Menu Guard)는 'vite:allowed:<uid>' 를 30분간 캐시한다.
 *  사이드바는 /api/menu 로 바로 갱신되므로, 메뉴가 바뀌면 가드 캐시가 옛것으로 남아
 *  "사이드바에는 보이는데 클릭하면 홈(denied=1)으로 튕기는" 상태가 된다.
 *  → 메뉴 변경을 감지했을 때 가드 캐시를 버려 다음 이동에서 새로 읽게 한다.
 */
function invalidateGuardCache(){
  try { if (currentUid) sessionStorage.removeItem(`vite:allowed:${currentUid}`); } catch { /* 무시 */ }
}

async function loadTree(force = false){
  if (!currentUid) currentUid = await currentUserId();
  cacheKey = `vite:sidebar:${currentUid}`;
  try {
    if (!force) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          tree.value = reorderAdminChildren(filterMenuItems(parsed));
          // 캐시된 트리로 경로 매핑 재구성
          pathToNode.clear();
          nodeToParents.clear();
          nodeToPath.clear();
          buildPathMappings(tree.value);
          sbReport('sessionStorage 캐시 사용', null, tree.value);
          revalidateTree();   // 캐시로 즉시 그리고, 뒤에서 최신 메뉴 확인 → 바뀌었으면 자동 갱신
          return;
        } catch { /* fallthrough */ }
      }
    }
    const token = localStorage.getItem('token');
    if (!token) { tree.value = []; return; }
    const headers = { Authorization: 'Bearer ' + token };
    const r = await fetch('/api/menu', { credentials: 'include', headers, cache: 'no-store' });
    const j = await r.json();
    if (j?.ok) {
      const t = (Array.isArray(j.tree) && j.tree.length) ? j.tree : buildTree(j.menus || []);
      const filtered = filterMenuItems(t);
      const next = reorderAdminChildren(filtered);
      const nextStr = JSON.stringify(next);
      // 내용이 그대로면 다시 그리지 않는다. (창 포커스마다 통째로 교체하면
      //  클릭하는 찰나에 메뉴가 새로 그려져 다른 항목이 눌릴 수 있다)
      if (tree.value.length && nextStr === sessionStorage.getItem(cacheKey)) {
        sbReport('재조회: 변경 없음(트리 유지)', j.menus, tree.value);
        return;
      }
      tree.value = next;
      sessionStorage.setItem(cacheKey, nextStr);
      invalidateGuardCache();   // 메뉴가 바뀌었으니 가드 캐시도 새로 읽게 한다
      sbReport(`API 조회(admin=${j.admin})`, j.menus, tree.value);

      // 경로 매핑 빌드 (성능 최적화 핵심)
      pathToNode.clear();
      nodeToParents.clear();
      nodeToPath.clear();
      buildPathMappings(tree.value);
    }
  } catch { /* ignore */ }
}

// 캐시로 먼저 그린 뒤 백그라운드로 서버 메뉴를 확인해, 달라졌으면(메뉴 추가/삭제/권한변경) 즉시 반영한다.
//  → 새 메뉴가 생겨도 탭을 닫거나 재로그인할 필요 없이 다음 화면 진입에서 자동으로 나타난다.
async function revalidateTree(){
  try {
    const token = localStorage.getItem('token');
    if (!token) return;
    const r = await fetch('/api/menu', { credentials: 'include', cache: 'no-store', headers: { Authorization: 'Bearer ' + token } });
    const j = await r.json();
    if (!j?.ok) return;
    const t = (Array.isArray(j.tree) && j.tree.length) ? j.tree : buildTree(j.menus || []);
    const next = reorderAdminChildren(filterMenuItems(t));
    const nextStr = JSON.stringify(next);
    if (nextStr === sessionStorage.getItem(cacheKey)) { if (SB_DEBUG()) sbReport('재검증: 변경 없음', j.menus, next); return; }
    tree.value = next;
    sessionStorage.setItem(cacheKey, nextStr);
    invalidateGuardCache();   // 메뉴가 바뀌었으니 가드 캐시도 새로 읽게 한다
    pathToNode.clear(); nodeToParents.clear(); nodeToPath.clear();
    buildPathMappings(tree.value);
    sbReport('메뉴 변경 감지 → 갱신', j.menus, tree.value);
  } catch (e) { sbLog('재검증 실패(캐시 유지):', e?.message || e); }
}

function onPermissionsChanged(e){
  try{
    const changedUserId = e?.detail?.userId;
    const adminChanged = !!(e?.detail?.adminChanged);
    const match = changedUserId ? (String(changedUserId) === String(currentUid)) : adminChanged;
    if (match){
      if (cacheKey) sessionStorage.removeItem(cacheKey);
      invalidateGuardCache();
      loadTree(true).then(()=> expandForPath(route.path));
    }
  }catch{ /* ignore */ }
}

// 쓰로틀링 설정
let lastLoadTime = 0;
const MIN_RELOAD_INTERVAL = 30000; // 30초

onMounted(async () => {
  currentUid = await currentUserId();
  await loadTree(false);
  expandForPath(route.path);
  window.addEventListener('menu-permissions-changed', onPermissionsChanged);

  // 쓰로틀링 적용된 focus/visibility 핸들러
  const onFocus = () => {
    try {
      const now = Date.now();
      if (now - lastLoadTime > MIN_RELOAD_INTERVAL) {
        lastLoadTime = now;
        loadTree(true).then(()=> expandForPath(route.path));
      }
    } catch {}
  };
  const onVisibility = () => {
    try {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        if (now - lastLoadTime > MIN_RELOAD_INTERVAL) {
          lastLoadTime = now;
          loadTree(true).then(()=> expandForPath(route.path));
        }
      }
    } catch {}
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisibility);
  // keep refs for cleanup
  window.__sidebar_onFocus = onFocus;
  window.__sidebar_onVisibility = onVisibility;
});

onBeforeUnmount(() => {
  window.removeEventListener('menu-permissions-changed', onPermissionsChanged);
  try { if (window.__sidebar_onFocus) window.removeEventListener('focus', window.__sidebar_onFocus); } catch {}
  try { if (window.__sidebar_onVisibility) document.removeEventListener('visibilitychange', window.__sidebar_onVisibility); } catch {}
});

// 현재 활성화된 노드의 모든 부모 ID를 computed로 캐싱
const activeParentIds = computed(() => {
  const currentPath = route.path;
  const activeNode = pathToNode.get(currentPath);

  if (!activeNode) return new Set();

  const parents = nodeToParents.get(activeNode.Id) || [];
  return new Set(parents.map(p => p.Id));
});

watch(() => route.path, (p) => { expandForPath(p); });

// 최적화된 hasActiveChild: O(N) 재귀 순회 → O(1) Set.has() 조회
function hasActiveChild(node){
  // 자기 자신이 활성화된 경우는 제외하고, 자식만 체크
  if (!Array.isArray(node.children) || node.children.length === 0) return false;

  // O(1) 조회: 현재 활성 노드의 부모들 중에 이 노드가 있는지 확인
  return activeParentIds.value.has(node.Id);
}

// 최적화된 expandForPath: O(N) 전체 순회 → O(depth) 역방향 탐색
function expandForPath(path){
  const activeNode = pathToNode.get(path);
  if (!activeNode) return;

  // 활성 노드의 부모들만 조회 (depth = 3~4)
  const parents = nodeToParents.get(activeNode.Id) || [];
  const keysToOpen = new Set(parents.map(p => keyOf(p)));

  openSet.value = new Set([ ...openSet.value, ...keysToOpen ]);
}
</script>

<style scoped>
/* 마지막 메뉴 항목이 사이드바 하단에 잘리지 않도록 여백 확보 */
.sidebar-nav{ padding-bottom: 32px; }
.sidebar-nav :deep(.list-group-item){ background:transparent; color:#fff; border:0; }
.sidebar-nav :deep(.list-group-item-action:hover){ background:rgba(255,255,255,.25); color:#fff; }
.sidebar-nav :deep(.list-group-item.active),
.sidebar-nav :deep(a.router-link-active.list-group-item){ background:#ffffff !important; color:#000000 !important; }
.sidebar-nav :deep(.list-group-item.active .text-truncate),
.sidebar-nav :deep(a.router-link-active.list-group-item .text-truncate){ color:#000000 !important; }
.sidebar-nav :deep(.btn-toggle){ background:transparent; color:#fff; border:0; text-align:left; transition: background-color 0.2s ease, border 0.2s ease; }
.sidebar-nav :deep(.btn-toggle.active),
.sidebar-nav :deep(.btn-toggle[aria-expanded="true"]){ background:#ffffff; color:#000000; }
.sidebar-nav :deep(.btn-toggle:hover){ background:rgba(255,255,255,.25) !important; color:#fff !important; }
.sidebar-nav :deep(.btn-toggle.parent-active){
  font-weight: 900 !important;
  font-style: italic !important;
}
.sidebar-nav :deep(.btn-toggle.parent-active) .text-truncate{
  padding-right: 6px !important;
  overflow: visible !important;
}
.sidebar-nav :deep(.btn-toggle.parent-active-child){
  font-style: italic !important;
}
.sidebar-nav :deep(.btn-toggle.parent-active-child) .text-truncate{
  padding-right: 6px !important;
  overflow: visible !important;
}
.sidebar-nav :deep(.text-truncate){ white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sidebar-nav :deep(.list-group > .list-group-item),
.sidebar-nav :deep(.list-group > .btn-toggle){ font-size: inherit; }
.sidebar-nav :deep(.list-group .list-group > .list-group-item),
.sidebar-nav :deep(.list-group .list-group > .btn-toggle){ font-size: calc(var(--sidebar-font-size) * 0.95); }
.sidebar-nav :deep(.list-group .list-group .list-group > .list-group-item),
.sidebar-nav :deep(.list-group .list-group .list-group > .btn-toggle){ font-size: calc(var(--sidebar-font-size) * 0.9); }
.sidebar-nav :deep(.list-group .list-group .list-group .list-group > .list-group-item),
.sidebar-nav :deep(.list-group .list-group .list-group .list-group > .btn-toggle){ font-size: calc(var(--sidebar-font-size) * 0.85); }
</style>
